/**
 * Remove untouched write-only fields (secret / password inputs) from a submit
 * payload. A write-only field loads empty and its stored value is masked on
 * read, so it is NEVER present in `initialValues`; the ResourcesSingle onSubmit
 * therefore injects it into the changed-values payload even when the user never
 * touched it — submitting `""` and silently OVERWRITING the real stored secret
 * on an unrelated edit. (Regression: `SECRET.value` was clobbered to an empty
 * envelope on a name-only edit — operator-task-fe-create-secret-menu-affordance.
 * `vault_config` etc. escaped it only because their form values happened to stay
 * unregistered; keying off the component closes the whole class robustly.)
 *
 * A field is write-only when its form component is `writeOnlyInput` (the general
 * signal, so a newly-added write-only resource cannot forget to register) or it
 * is listed in the per-resource write-only allowlist. An empty/blank value for
 * such a field means "untouched — do not change"; only a value the user actually
 * typed is kept.
 *
 * Kept in a standalone module (no heavy config/router imports) so it can be
 * unit-tested in isolation.
 */
export const stripUntouchedWriteOnlyFields = (
    changedValues: { [key: string]: any },
    formFields: Array<{ component?: string; name?: string }> | undefined,
    writeOnlyFieldsWithData: { [key: string]: boolean } = {}
): { [key: string]: any } => {
    const writeOnlyFieldNames = new Set(Object.keys(writeOnlyFieldsWithData));
    for (const field of formFields ?? []) {
        if (field?.component === "writeOnlyInput" && field?.name) {
            writeOnlyFieldNames.add(field.name);
        }
    }
    const result = { ...changedValues };
    for (const name of writeOnlyFieldNames) {
        if (name in result && (result[name] === "" || result[name] == null)) {
            delete result[name];
        }
    }
    return result;
};
